import React from 'react';
import StepIndicator from './StepIndicator';
import { Icon } from '../../../elements/icon';
import { useTranslation } from 'react-i18next';

function ResultsScreen({
  onBack,
  onClose,
  onBookReview,
  onCancelAnyway,
  isSubmitting,
  reviewNote,
  onChangeReviewNote,
  canRequestReview = true,
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState(false);
  const hasReviewNote = String(reviewNote || '').trim().length > 0;

  const handleSelect = () => {
    setSelected(true);
  };

  return (
    <>
      <StepIndicator step={2} onBack={onBack} showBack onClose={onClose} />

      <div className="t-mb-4">
        <h2 className="t-text-[26px] t-font-serif t-text-slate-900 t-leading-tight t-mb-2">
          {t('CancelFlow_Results_Title_Line1')}
          <br />
          <em className="t-text-secondary">{t('CancelFlow_Results_Title_Line2')}</em>
        </h2>
        <p className="t-text-sm t-text-slate-600 t-m-0">{t('CancelFlow_Results_Subtitle')}</p>
      </div>

      <div className="t-relative t-mb-4 t-pl-1">
        {/* Vertical connector line between dots */}
        <div
          className="t-absolute t-w-0.5 t-bg-slate-200 t-left-[10px] t-top-1 t-bottom-0 t-h-[80%]"
          aria-hidden="true"
        />
        <div className="t-flex t-flex-col t-gap-3">
          <div className="t-flex t-items-start t-gap-3 t-relative t-pb-3">
            <div className="t-w-[14px] t-h-[14px] t-rounded-full t-bg-slate-200 t-mt-[2px] t-shrink-0" />
            <div className="t-flex-1">
              <div className="t-text-[13px] t-font-semibold t-text-slate-400">
                {t('CancelFlow_Results_Month1_Title')}
              </div>
              <span className="t-text-[12px] t-text-slate-400">
                {t('CancelFlow_Results_Month1_Subtitle')}
              </span>
            </div>
          </div>
          <div className="t-flex t-items-start t-gap-3 t-relative t-pb-3">
            <div className="t-w-[14px] t-h-[14px] t-rounded-full t-bg-slate-200 t-mt-[2px] t-shrink-0" />
            <div className="t-flex-1">
              <div className="t-text-[13px] t-font-semibold t-text-slate-400">
                {t('CancelFlow_Results_Month2_Title')}
              </div>
              <span className="t-text-[12px] t-text-slate-400">
                {t('CancelFlow_Results_Month2_Subtitle')}
              </span>
            </div>
          </div>
          <div className="t-flex t-items-start t-gap-3 t-relative">
            <div className="t-w-[14px] t-h-[14px] t-rounded-full t-bg-secondary t-mt-[2px] t-shadow-[0_0_0_4px_rgba(26,107,90,0.15)] t-shrink-0" />
            <div className="t-flex-1">
              <div className="t-text-[13px] t-font-semibold t-text-slate-900">
                {t('CancelFlow_Results_Month3_Title')}
              </div>
              <span className="t-text-[12px] t-text-slate-600">
                {t('CancelFlow_Results_Month3_Subtitle')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="t-mb-3">
        <button
          type="button"
          onClick={handleSelect}
          className={`t-w-full t-flex t-items-center t-gap-3 t-px-4 t-py-3 t-rounded-xl t-border-2 t-transition
          ${
            selected
              ? 't-border-secondary t-bg-teal-bg'
              : 't-border-teal-mid hover:t-border-secondary hover:t-bg-teal-bg'
          }`}
        >
          <div
            className={`t-w-10 t-h-10 t-rounded-xl t-flex t-items-center t-justify-center t-bg-teal-bg t-text-secondary t-shrink-0 t-transition ${
              selected ? '!t-bg-secondary t-text-white' : ''
            }`}
          >
            <span className="t-text-base">
              <Icon name="person" color={selected ? '#fff' : '#005d67'} />
            </span>
          </div>
          <div className="t-flex-1 t-text-left">
            <div className="t-text-sm t-font-semibold t-text-slate-900">
              {t('CancelFlow_Results_CardTitle')}
            </div>
            <div className="t-text-xs t-text-slate-600">{t('CancelFlow_Results_CardSubtitle')}</div>
          </div>
          <div className="t-text-[11px] t-font-semibold t-text-secondary t-bg-teal-bg t-border t-border-secondary t-rounded-full t-px-2 t-py-0.5">
            {t('CancelFlow_Results_CardBadge')}
          </div>
        </button>
      </div>

      <div className="t-flex t-items-center t-gap-2 t-bg-[#EAF4F1] t-border-l-4 t-border-secondary t-rounded-lg t-px-3 t-py-2 t-text-[12px] t-text-slate-700 t-mb-4">
        <span className="t-text-secondary">📈</span>
        <span>{t('CancelFlow_Results_Info')}</span>
      </div>

      {selected && (
        <div className="t-mb-4">
          <label className="t-block t-text-xs t-font-semibold t-text-slate-700 t-mb-1">
            {t('CancelFlow_Results_ReviewNote_Label')}
          </label>
          <textarea
            value={reviewNote}
            onChange={(event) => onChangeReviewNote?.(event.target.value)}
            rows={4}
            maxLength={1200}
            disabled={isSubmitting || !canRequestReview}
            className="t-w-full t-rounded-xl t-border t-border-teal-mid t-bg-white t-px-3 t-py-2 t-text-sm t-text-slate-800 placeholder:t-text-slate-400 focus:t-border-secondary focus:t-outline-none focus:t-ring-2 focus:t-ring-secondary/20 disabled:t-bg-slate-100 disabled:t-cursor-not-allowed"
            placeholder={t('CancelFlow_Results_ReviewNote_Placeholder')}
          />
          {!hasReviewNote && (
            <p className="t-m-0 t-mt-1 t-text-[11px] t-text-slate-500">
              {t('CancelFlow_Results_ReviewNote_Required')}
            </p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={onBookReview}
        disabled={!selected || !hasReviewNote || isSubmitting || !canRequestReview}
        className="t-w-full t-inline-flex t-items-center t-justify-center t-rounded-xl t-px-4 t-py-3 t-bg-secondary t-text-white t-font-semibold t-text-sm t-gap-2 t-shadow-sm hover:t-shadow-[0_4px_12px_rgba(26,107,90,0.28)] t-transition t-mb-4 disabled:t-opacity-50 disabled:t-cursor-not-allowed"
      >
        <span>{t('CancelFlow_Results_BookReview')}</span>
        <span aria-hidden="true">✓</span>
      </button>

      <button
        type="button"
        onClick={onCancelAnyway}
        disabled={isSubmitting}
        className="t-block t-w-full t-text-center t-text-sm t-text-slate-600 hover:t-text-slate-800 t-mb-4"
      >
        {t('CancelFlow_Results_ConfirmCancel')}
      </button>

      <button
        type="button"
        onClick={onClose}
        disabled={isSubmitting}
        className="t-block t-w-full t-text-center t-text-xs t-text-slate-400"
      >
        {t('CancelFlow_Results_NeverMind')}
      </button>
    </>
  );
}

export default ResultsScreen;
